import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Container,
  Table,
  Form,
  InputGroup,
  Pagination,
  Toast,
  ToastContainer
} from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axios from "axios";
import * as XLSX from 'xlsx';
import {
  ArrowLeft,
  ClockHistory,
  Calendar,
  Cash,
  CheckCircle,
  InfoCircle,
  Person,
  Search,
  FileText,
  Filter,
  Printer,
  Download,
  Bank,
  Phone,
  CreditCard,
  GraphUp,
  Wallet2,
  XCircle,
  Award,
  CurrencyDollar,
  FileEarmarkExcel,
  FilePdf
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

const BonusHistory = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  
  // États pour les filtres appliqués
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  
  // États pour les filtres temporaires (avant validation)
  const [tempStartDate, setTempStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [tempEndDate, setTempEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempPerPage, setTempPerPage] = useState(10);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [toastTitle, setToastTitle] = useState('');

  // Récupérer la devise depuis la session
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  const showToastNotification = (title, message, variant = 'success') => {
    setToastTitle(title);
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  // 🔥 Formater la période pour l'affichage
  const formatPeriod = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} - ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  };

  // Charger les données
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        date_debut: startDate,
        date_fin: endDate,
        ...(searchTerm && { search: searchTerm }),
        sort_field: 'created_at',
        sort_direction: 'desc'
      });

      const response = await axios.get(
        `${API_BASE_URL}/agent-finance/payment-history?${params}`,
        { headers: getHeaders() }
      );
      
      const data = response.data.data || response.data;
      setHistory(data.logs || []);
      setStats(data.stats || null);
      setTotalPages(data.pagination?.last_page || 1);
      
    } catch (err) {
      console.error("Erreur chargement:", err);
      showToastNotification('❌ Erreur', err.response?.data?.message || 'Erreur lors du chargement des données', 'danger');
      setHistory([]);
      setStats(null);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, startDate, endDate, searchTerm]);

  // 🔥 APPLIQUER LES FILTRES
  const handleApplyFilters = useCallback(async () => {
    // Valider les dates temporaires
    if (!tempStartDate || !tempEndDate) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
      return;
    }
    
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      showToastNotification('⚠️ Erreur', 'Format de date invalide', 'danger');
      return;
    }
    
    if (start > end) {
      showToastNotification('⚠️ Attention', 'La date de début ne peut pas être après la date de fin', 'warning');
      return;
    }
    
    // Appliquer les filtres
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSearchTerm(tempSearchTerm);
    setPerPage(tempPerPage);
    setCurrentPage(1);
    setIsFilterApplied(true);
    
    showToastNotification('✅ Filtres appliqués', 'Chargement des données avec les nouveaux filtres...', 'info');
    
    // Recharger avec les nouveaux filtres
    await fetchData();
    
    showToastNotification('✅ Succès', 'Les filtres ont été appliqués avec succès', 'success');
  }, [tempStartDate, tempEndDate, tempSearchTerm, tempPerPage, fetchData]);

  // 🔥 RÉINITIALISER LES FILTRES
  const handleResetFilters = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    
    setTempStartDate(today);
    setTempEndDate(today);
    setTempSearchTerm("");
    setTempPerPage(10);
    
    setStartDate(today);
    setEndDate(today);
    setSearchTerm("");
    setPerPage(10);
    setCurrentPage(1);
    setIsFilterApplied(false);
    
    showToastNotification('🔄 Réinitialisation', 'Réinitialisation des filtres en cours...', 'info');
    
    fetchData();
    
    showToastNotification('🔄 Filtres réinitialisés', 'Les filtres ont été réinitialisés', 'info');
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'cash': <Cash size={14} />,
      'bank_transfer': <Bank size={14} />,
      'wave': <Phone size={14} />,
      'orange_money': <Phone size={14} />,
      'mtn_money': <Phone size={14} />,
      'credit_card': <CreditCard size={14} />
    };
    return icons[method] || <Wallet2 size={14} />;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'cash': 'Espèces',
      'bank_transfer': 'Virement bancaire',
      'wave': 'Wave',
      'orange_money': 'Orange Money',
      'mtn_money': 'MTN Mobile Money',
      'credit_card': 'Carte bancaire',
      'cheque': 'Chèque'
    };
    return labels[method] || method || 'N/A';
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Col md={3} sm={6} className="mb-3">
      <Card className="h-100 border-0 shadow-sm stats-card">
        <div className={`card-gradient bg-${color}`}></div>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6 className="text-muted mb-2 small text-uppercase fw-bold">{title}</h6>
              <h3 className="mb-0 fw-bold">{value}</h3>
              {subtitle && <small className="text-muted">{subtitle}</small>}
            </div>
            <div className={`bg-${color} bg-opacity-10 rounded-circle p-3`}>
              {icon}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(Math.max(1, currentPage - 1))} />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
      );
    }

    return items;
  };

  // ==================== EXPORT EXCEL ====================
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams({
        page: 1,
        per_page: 10000,
        date_debut: startDate,
        date_fin: endDate,
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(
        `${API_BASE_URL}/agent-finance/payment-history?${params}`,
        { headers: getHeaders() }
      );
      
      const data = response.data.data || response.data;
      const allLogs = data.logs || [];
      
      if (allLogs.length === 0) {
        showToastNotification('⚠️ Attention', 'Aucune donnée à exporter', 'warning');
        setExporting(false);
        return;
      }

      const excelData = allLogs.map(item => ({
        'Agent': item.agent?.name || item.agent_name || `Agent ${item.user_id}`,
        'Email': item.agent?.email || 'N/A',
        'Type de prime': item.bonus_type_label || (item.bonus_type === 'enrollement' ? 'Enrôlement' : 'Mobilisation'),
        'Période': item.period_month || 'N/A',
        'Montant': item.amount || 0,
        'Méthode': getPaymentMethodLabel(item.payment_method),
        'Référence': item.reference || 'N/A',
        'Date paiement': formatDate(item.processed_at || item.created_at),
        'Traité par': item.processor_name || item.processed_by || 'N/A'
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      ws['!cols'] = [
        { wch: 30 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
        { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 20 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Historique des primes');
      
      const fileName = `historique_primes_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showToastNotification('✅ Succès', 'Export Excel terminé avec succès', 'success');
    } catch (error) {
      console.error("Erreur export Excel:", error);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export Excel: ' + error.message, 'danger');
    } finally {
      setExporting(false);
    }
  };

  // ==================== EXPORT PDF ====================
  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    
    try {
      const params = new URLSearchParams({
        page: 1,
        per_page: 10000,
        date_debut: startDate,
        date_fin: endDate,
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(
        `${API_BASE_URL}/agent-finance/payment-history?${params}`,
        { headers: getHeaders() }
      );
      
      const data = response.data.data || response.data;
      const allLogs = data.logs || [];
      const statsData = data.stats || null;
      
      if (allLogs.length === 0) {
        showToastNotification('⚠️ Attention', 'Aucune donnée à exporter', 'warning');
        setExporting(false);
        return;
      }

      const fileName = `HISTORIQUE_PRIMES_${new Date().toISOString().split('T')[0]}`;
      const originalTitle = document.title;
      document.title = fileName;
      
      const formatCurrencyPDF = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
      };
      
      const formatDatePDF = (dateString) => {
        if (!dateString) return "-";
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        } catch (e) { 
          return dateString; 
        }
      };
      
      const getPaymentMethodLabelPDF = (method) => {
        const labels = {
          'cash': 'Espèces',
          'bank_transfer': 'Virement bancaire',
          'wave': 'Wave',
          'orange_money': 'Orange Money',
          'mtn_money': 'MTN Mobile Money',
          'credit_card': 'Carte bancaire',
          'cheque': 'Chèque'
        };
        return labels[method] || method || 'N/A';
      };

      const getBonusTypeLabelPDF = (type) => {
        return type === 'enrollement' ? "Prime d'enrôlement" : "Prime de mobilisation";
      };

      const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: Arial, Helvetica, sans-serif; 
      padding: 4mm 5mm;
      background: white;
      font-size: 9pt;
      line-height: 1.25;
      color: #1a1a1a;
    }
    .report {
      max-width: 100%;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      padding-bottom: 8px;
      border-bottom: 3px solid #1a3a6a;
      margin-bottom: 12px;
      position: relative;
    }
    .header .top-line {
      font-size: 8pt;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 2px;
    }
    .header h1 {
      font-size: 16pt;
      font-weight: bold;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #1a3a6a;
      margin: 2px 0;
    }
    .header .subtitle {
      font-size: 10pt;
      color: #444;
      margin-top: 2px;
    }
    .header .meta {
      font-size: 8pt;
      color: #555;
      margin-top: 4px;
      display: flex;
      justify-content: center;
      gap: 20px;
    }
    .section {
      margin-bottom: 10px;
    }
    .section-title {
      font-size: 10pt;
      font-weight: bold;
      text-transform: uppercase;
      border-bottom: 2px solid #999;
      padding-bottom: 3px;
      margin-bottom: 6px;
      color: #1a3a6a;
      letter-spacing: 0.5px;
    }
    .stats-grid {
      display: table;
      width: 100%;
      border-collapse: collapse;
    }
    .stats-row {
      display: table-row;
    }
    .stat-item {
      display: table-cell;
      padding: 3px 6px;
      border-bottom: 1px solid #eee;
      vertical-align: middle;
      width: 25%;
    }
    .stat-item .label {
      font-size: 8pt;
      color: #444;
    }
    .stat-item .value {
      font-size: 10pt;
      font-weight: bold;
      text-align: right;
    }
    .stat-item .value.green { color: #1a7a3a; }
    .stat-item .value.blue { color: #1a3a6a; }
    .stat-item .value.orange { color: #b85a1a; }
    .stat-item .value.red { color: #8a1a1a; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8pt;
      margin-top: 4px;
    }
    table thead th {
      background: #1a3a6a;
      color: white;
      padding: 4px 6px;
      text-align: left;
      font-weight: bold;
      border: 1px solid #1a3a6a;
      font-size: 7.5pt;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    table tbody td {
      padding: 3px 6px;
      border: 1px solid #ddd;
      font-size: 8pt;
    }
    table tbody tr:nth-child(even) {
      background: #f8f9fa;
    }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: bold; }
    .badge {
      display: inline-block;
      padding: 1px 8px;
      border-radius: 3px;
      font-size: 7.5pt;
      font-weight: bold;
    }
    .badge.enrollement { background: #d1ecf1; color: #0c5460; }
    .badge.mobilisation { background: #fff3cd; color: #856404; }
    .footer {
      margin-top: 15px;
      padding-top: 6px;
      border-top: 1px solid #ccc;
      font-size: 7.5pt;
      color: #777;
      display: flex;
      justify-content: space-between;
    }
    @media print {
      body { padding: 3mm 4mm; }
      .stat-item { break-inside: avoid; }
      table { break-inside: auto; }
      table thead th { background: #1a3a6a !important; color: white !important; }
    }
  </style>
</head>
<body>
  <div class="report">
    <div class="header">
      <div class="top-line">● ● ● RÉPUBLIQUE DÉMOCRATIQUE DU CONGO ● ● ●</div>
      <h1>Historique des paiements de primes</h1>
      <div class="subtitle">Gestion des primes d'enrôlement et de mobilisation</div>
      <div class="meta">
        <span>Période: ${formatPeriod()}</span>
        <span>|</span>
        <span>${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
        <span>|</span>
        <span>${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">1. Synthèse des indicateurs</div>
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total paiements</span></div>
          <div class="stat-item"><span class="value blue">${statsData?.total_payments || allLogs.length}</span></div>
          <div class="stat-item"><span class="label">Montant total</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(statsData?.total_amount || 0)}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Primes d'enrôlement</span></div>
          <div class="stat-item"><span class="value blue">${formatCurrencyPDF(statsData?.enrolment_total || 0)}</span></div>
          <div class="stat-item"><span class="label">Primes de mobilisation</span></div>
          <div class="stat-item"><span class="value orange">${formatCurrencyPDF(statsData?.mobilisation_total || 0)}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Nombre d'agents</span></div>
          <div class="stat-item"><span class="value blue">${statsData?.agents_count || 0}</span></div>
          <div class="stat-item"><span class="label">Période</span></div>
          <div class="stat-item"><span class="value">${formatPeriod()}</span></div>
        </div>
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">2. Liste des paiements</div>
      <table>
        <thead>
          <tr>
            <th style="width:30px;">#</th>
            <th style="width:15%;">Agent</th>
            <th style="width:13%;">Type</th>
            <th style="width:10%;">Période</th>
            <th style="width:12%;">Montant</th>
            <th style="width:15%;">Référence</th>
            <th style="width:13%;">Date paiement</th>
            <th style="width:10%;">Traité par</th>
          </tr>
        </thead>
        <tbody>
          ${allLogs.map((item, index) => `
            <tr>
              <td class="text-center">${index + 1}</td>
              <td><strong>${item.agent?.name || item.agent_name || `Agent ${item.user_id}`}</strong></td>
              <td><span class="badge ${item.bonus_type === 'enrollement' ? 'enrollement' : 'mobilisation'}">${getBonusTypeLabelPDF(item.bonus_type)}</span></td>
              <td>${item.period_month || '-'}</td>
              <td class="text-right fw-bold">${formatCurrencyPDF(item.amount)}</td>
              <td><code style="font-size:7pt;">${item.reference || '-'}</code></td>
              <td>${formatDatePDF(item.processed_at || item.created_at)}</td>
              <td>${item.processor_name || item.processed_by || '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <p style="margin-top:4px; font-size:7pt; color:#888; text-align:right;">
        Total: ${allLogs.length} paiement(s)
      </p>
    </div>
    
    <div class="footer">
      <span>Généré par SyncTaxe</span>
      <span>${new Date().toLocaleDateString('fr-FR')} - ${new Date().toLocaleTimeString('fr-FR')}</span>
    </div>
    
  </div>
</body>
</html>`;
    
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
      
      iframe.contentWindow.onload = () => {
        iframe.contentWindow.document.title = fileName;
        iframe.contentWindow.print();
        
        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExporting(false);
          showToastNotification('✅ Succès', 'PDF exporté avec succès', 'success');
        }, 1500);
      };
      
      iframe.onerror = () => {
        document.title = originalTitle;
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
        setExporting(false);
        showToastNotification('❌ Erreur', 'Erreur lors de l\'export PDF', 'danger');
      };
      
    } catch (err) {
      console.error('Erreur export PDF:', err);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export PDF: ' + err.message, 'danger');
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Card className="border-0 shadow-lg rounded-4 text-center" style={{ maxWidth: '500px', width: '100%' }}>
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="pulse-animation mx-auto d-flex align-items-center justify-content-center" 
                       style={{ 
                         width: '90px', 
                         height: '90px', 
                         background: 'linear-gradient(135deg, #0d6efd, #0a58ca)', 
                         borderRadius: '50%',
                         boxShadow: '0 10px 40px rgba(13, 110, 253, 0.3)'
                       }}>
                    <ClockHistory size={40} className="text-white" />
                  </div>
                </div>
                
                <h4 className="fw-bold mb-2">Chargement en cours</h4>
                <p className="text-muted mb-4">
                  Récupération de l'historique des paiements...
                </p>
                
                <div className="d-flex justify-content-center gap-2 mb-4">
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.15s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.3s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.45s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.6s' }} />
                </div>
                
                <div className="mx-auto" style={{ width: '200px' }}>
                  <div className="progress" style={{ height: '4px', borderRadius: '2px' }}>
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <small className="text-muted">
                    <InfoCircle className="me-1" size={12} />
                    Cette opération peut prendre quelques secondes
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Container>
          
          <style>{`
            .pulse-animation {
              animation: pulse 1.5s ease-in-out infinite;
            }
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.4); }
              50% { transform: scale(1.05); opacity: 0.9; box-shadow: 0 0 0 20px rgba(13, 110, 253, 0); }
              100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0); }
            }
          `}</style>
          
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          {/* Toast */}
          <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
            <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant} delay={5000} autohide>
              <Toast.Header>
                <strong className="me-auto">{toastTitle}</strong>
              </Toast.Header>
              <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>
                {toastMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>

          {/* En-tête avec boutons d'export */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><a href="#" onClick={() => navigate('/superAdmin/Agents')}>Agents</a></li>
                    <li className="breadcrumb-item active" aria-current="page">Historique des paiements</li>
                  </ol>
                  <h2 className="mb-0 fw-bold">
                    <ClockHistory className="me-2 text-primary" />
                    Historique des paiements de primes
                  </h2>
                  <p className="text-muted mb-0">
                    Historique complet de tous les paiements de primes effectués
                  </p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button 
                    variant="success" 
                    className="d-flex align-items-center"
                    onClick={handleExportExcel}
                    disabled={exporting || history.length === 0}
                  >
                    {exporting ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      <FileEarmarkExcel className="me-2" size={18} />
                    )}
                    Excel
                  </Button>
                  <Button 
                    variant="danger" 
                    className="d-flex align-items-center"
                    onClick={handleExportPDF}
                    disabled={exporting || history.length === 0}
                  >
                    {exporting ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      <FilePdf className="me-2" size={18} />
                    )}
                    PDF
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Filtres - Version avec validation manuelle */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" />
                          Date de début
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={tempStartDate}
                          onChange={(e) => setTempStartDate(e.target.value)}
                          className={`bg-light border-0 ${!tempStartDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" />
                          Date de fin
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={tempEndDate}
                          onChange={(e) => setTempEndDate(e.target.value)}
                          className={`bg-light border-0 ${!tempEndDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Search className="me-1" />
                          Rechercher
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light border-0">
                            <Search />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Agent, référence..."
                            value={tempSearchTerm}
                            onChange={(e) => setTempSearchTerm(e.target.value)}
                            className="bg-light border-0"
                          />
                          {tempSearchTerm && (
                            <Button 
                              variant="outline-secondary" 
                              className="border-0"
                              onClick={() => setTempSearchTerm("")}
                            >
                              <XCircle size={16} />
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">Par page</Form.Label>
                        <Form.Select
                          value={tempPerPage}
                          onChange={(e) => setTempPerPage(Number(e.target.value))}
                          className="bg-light border-0"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                       <Col md={3}>
                         <Form.Label className="fw-bold small"></Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button 
                          variant="primary" 
                          onClick={handleApplyFilters}
                          className="d-flex align-items-center"
                          disabled={loading}
                        >
                          {loading ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <Search className="me-2" size={16} />
                          )}
                          Valider les filtres
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleResetFilters}
                          className="d-flex align-items-center"
                          disabled={loading}
                        >
                          <ArrowLeft className="me-2" size={16} />
                          
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Boutons d'action des filtres */}
               
                  
                  {/* Indicateur des filtres actifs */}
                  <Row className="mt-2">
                    <Col md={12}>
                      <small className="text-muted">
                        <strong>Période :</strong> {formatPeriod()}
                        {searchTerm && <Badge bg="info" className="ms-2">Filtre: {searchTerm}</Badge>}
                        {isFilterApplied && <Badge bg="secondary" className="ms-2">Filtres appliqués</Badge>}
                      </small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Statistiques */}
          {stats && (
            <Row className="g-3 mb-4">
              <StatCard
                title="Total des paiements"
                value={stats.total_payments}
                icon={<FileText className="text-primary" size={22} />}
                color="primary"
              />
              <StatCard
                title="Montant total"
                value={formatCurrency(stats.total_amount)}
                icon={<CurrencyDollar className="text-success" size={22} />}
                color="success"
              />
              <StatCard
                title="Primes d'enrôlement"
                value={formatCurrency(stats.enrolment_total)}
                icon={<Award className="text-info" size={22} />}
                color="info"
              />
              <StatCard
                title="Primes de mobilisation"
                value={formatCurrency(stats.mobilisation_total)}
                icon={<GraphUp className="text-warning" size={22} />}
                color="warning"
              />
            </Row>
          )}

          {/* Tableau */}
          <Card className="shadow-sm border-0 rounded-3">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3 border-bottom">
              <h5 className="mb-0 fw-bold">
                <ClockHistory className="me-2" />
                Historique des paiements
              </h5>
              <div className="d-flex align-items-center gap-2">
                {isFilterApplied && (
                  <Badge bg="secondary" className="px-3 py-2 rounded-pill">
                    Filtres actifs
                  </Badge>
                )}
                <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill">
                  {history.length} paiement(s)
                </Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {history.length === 0 ? (
                <div className="text-center py-5">
                  <InfoCircle size={48} className="text-muted opacity-25" />
                  <h5 className="mt-3 text-muted">AUCUN PAIEMENT TROUVÉ</h5>
                  <p className="text-muted small">
                    {searchTerm || (startDate && endDate) ? (
                      "Aucun paiement ne correspond à vos critères"
                    ) : (
                      "Aucun paiement enregistré"
                    )}
                  </p>
                  {(searchTerm || (startDate && endDate)) && (
                    <Button variant="outline-primary" size="sm" onClick={handleResetFilters}>
                      Réinitialiser les filtres
                    </Button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Agent</th>
                        <th>Type de prime</th>
                        <th>Période</th>
                        <th>Montant</th>
                        <th>Référence</th>
                        <th>Date paiement</th>
                        <th>Traité par</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
                                <Person size={14} className="text-primary" />
                              </div>
                              <div>
                                <strong>{item.agent?.name || item.agent_name || `Agent ${item.user_id}`}</strong>
                                <br />
                                <small className="text-muted">{item.agent?.email || 'N/A'}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg={item.bonus_type === 'enrollement' ? 'info' : 'warning'} pill className="px-3 py-2">
                              {item.bonus_type_label || (item.bonus_type === 'enrollement' ? "Enrôlement" : "Mobilisation")}
                            </Badge>
                          </td>
                          <td>
                            <span className="text-muted">{item.period_month}</span>
                          </td>
                          <td>
                            <strong className="text-success">{formatCurrency(item.amount)}</strong>
                          </td>
                          <td>
                            <code className="bg-light p-1 rounded">{item.reference}</code>
                          </td>
                          <td>
                            <small className="text-muted">{formatDate(item.processed_at || item.created_at)}</small>
                          </td>
                          <td>
                            <span className="text-muted">{item.processor_name || item.processor_by || 'N/A'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                  <div className="text-muted small">
                    Affichage de <strong>{(currentPage - 1) * perPage + 1}</strong> à <strong>{Math.min(currentPage * perPage, history.length)}</strong> sur <strong>{history.length}</strong> paiements
                  </div>
                  <Pagination className="mb-0">
                    {getPaginationItems()}
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

        <style>{`
          .stats-card { 
            transition: transform 0.3s ease, box-shadow 0.3s ease; 
            border-radius: 12px; 
            overflow: hidden; 
            position: relative;
          }
          .stats-card:hover { 
            transform: translateY(-5px); 
            box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; 
          }
          .card-gradient { 
            position: absolute; 
            top: 0; 
            left: 0; 
            width: 100%; 
            height: 4px; 
            opacity: 0.8; 
          }
          .form-control:focus, .form-select:focus { 
            border-color: #0d6efd; 
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25); 
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
          }
          .btn-sm {
            padding: 0.25rem 0.5rem;
          }
          .pulse-animation {
            animation: pulse 1.5s ease-in-out infinite;
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.4); }
            50% { transform: scale(1.05); opacity: 0.9; box-shadow: 0 0 0 20px rgba(13, 110, 253, 0); }
            100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13, 110, 253, 0); }
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
};

export default BonusHistory;